import { createHashRouter, RouterProvider, Navigate } from 'react-router-dom';
import { useAuthBootstrap } from './auth/bootstrap.tsx';
import { LoginModal } from './auth/LoginModal.tsx';
import { RequireAuth } from './auth/RequireAuth.tsx';
import { Shell } from './layout/AppShell.tsx';
import { HomePage } from './pages/Home.tsx';
import { GenericCrud } from './pages/reference/GenericCrud.tsx';
import {
  countriesConfig,
  languagesConfig,
  locationsConfig,
  accomodationPlacesConfig,
  reportMailsConfig,
} from './pages/reference/configs.ts';
import { RolesPage } from './pages/reference/Roles.tsx';
import { VisitorsListPage } from './pages/Visitors/List.tsx';
import { SettingsPage } from './pages/Account/Settings.tsx';
import { ChangePasswordPage } from './pages/Account/ChangePassword.tsx';
import { SocialRegisterPage } from './pages/SocialRegister.tsx';
import { TravelsPage } from './pages/Travels.tsx';
import { GovFormPage } from './pages/GovForm.tsx';
import { TrainBookingPage } from './pages/TrainBooking.tsx';
import { VisitorGroupsPage } from './pages/VisitorGroups.tsx';
import { UsersPage } from './pages/Users.tsx';

/**
 * Hash router preserves the legacy `/#/foo` URLs, which matters for the
 * Google OAuth callback (`/#/social-register/google?success=true`) — the
 * backend's redirect URL doesn't have to change.
 */
const router = createHashRouter([
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: 'visitors', element: <VisitorsListPage /> },
      // Deep-link to a visitor → bounce home; the daily-driver page is the canonical edit surface.
      { path: 'visitors/:id', element: <Navigate to="/" replace /> },
      { path: 'visitor-groups', element: <VisitorGroupsPage /> },
      { path: 'visitor-groups/:id', element: <VisitorGroupsPage /> },
      { path: 'travels', element: <TravelsPage /> },
      { path: 'gov-form', element: <GovFormPage /> },
      { path: 'train', element: <TrainBookingPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'password', element: <ChangePasswordPage /> },
      { path: 'countries', element: <GenericCrud config={countriesConfig} /> },
      { path: 'languages', element: <GenericCrud config={languagesConfig} /> },
      { path: 'locations', element: <GenericCrud config={locationsConfig} /> },
      { path: 'accomodation-places', element: <GenericCrud config={accomodationPlacesConfig} /> },
      { path: 'roles', element: <RolesPage /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'report-mails', element: <GenericCrud config={reportMailsConfig} /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  // Public (NOT behind RequireAuth): the OAuth landing runs for anonymous users
  // too — on a failed sign-in the user has no session, and gating this route
  // would render nothing (swallowing the error toast + redirect). The page
  // hydrates the session on success and bounces to '/' either way.
  { path: '/social-register/:provider', element: <SocialRegisterPage /> },
]);

export function App() {
  const { loading } = useAuthBootstrap();
  if (loading) return null; // brief blank flash beats a flicker of "Sign in" → home
  return (
    <>
      <RouterProvider router={router} />
      <LoginModal />
    </>
  );
}
